import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/shared/Header";
import { IssueReportsPanel } from "@/components/admin/IssueReportsPanel";
import { ReviewsPanel } from "@/components/admin/ReviewsPanel";
import { ErrorLogsPanel } from "@/components/admin/ErrorLogsPanel";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import videoApi from "@/lib/api";
import { getApiErrorMessage } from "@/lib/api";
import { getApiBaseUrl } from "@/lib/config";
import {
  Users,
  Video,
  CheckCircle,
  Clock,
  XCircle,
  Crown,
  Trash2,
  Eye,
  RefreshCw,
  Loader2,
  Shield,
  DollarSign,
  TrendingUp,
  Cpu,
  HardDrive,
  Activity,
  Server,
  AlertTriangle,
  Gauge,
  Search,
  Bug,
  Star,
  Download,
} from "lucide-react";
import { formatDate, formatDuration } from "@/lib/utils";
import { logActionError } from "@/lib/errorLogging";

const API = getApiBaseUrl();

const AdminDashboardPage = () => {
  const { isAdmin } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [costs, setCosts] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [r2Status, setR2Status] = useState(null);
  const [liveMonitor, setLiveMonitor] = useState(null);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [issueReports, setIssueReports] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [errorLogsTotal, setErrorLogsTotal] = useState(0);
  const [errorLogsUnseen, setErrorLogsUnseen] = useState(0);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [deletingErrorLogs, setDeletingErrorLogs] = useState(false);
  const [videosLoading, setVideosLoading] = useState(false);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [consoleCleanupLoading, setConsoleCleanupLoading] = useState(false);
  const [videoCleanupLoadingId, setVideoCleanupLoadingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  // Subscriptions that deleting `userToDelete` cancels; null while Mercado Pago is still being asked.
  const [userToDeleteSubscriptions, setUserToDeleteSubscriptions] = useState([]);
  const deleteDialogUserIdRef = useRef(null);
  const [pendingMembershipChange, setPendingMembershipChange] = useState(null);
  const [changingMembership, setChangingMembership] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const selectedUserData = selectedUser?.user || null;

  const fetchStats = useCallback(async ({ showErrorToast = true } = {}) => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar las estadísticas" : "Failed to load statistics", language));
      }
    }
  }, [language]);

  const fetchCosts = useCallback(async () => {
    try {
      const data = await videoApi.getAdminCosts();
      setCosts(data);
    } catch (error) {
      console.error("Failed to fetch costs:", error);
    }
  }, []);

  const fetchCapacity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/system-capacity`);
      setCapacity(response.data);
    } catch (error) {
      console.error("Failed to fetch capacity:", error);
    }
  }, []);

  const fetchR2Status = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/r2-status`);
      setR2Status(response.data);
    } catch (error) {
      console.error("Failed to fetch R2 status:", error);
    }
  }, []);

  const fetchLiveMonitor = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/live-monitor`);
      setLiveMonitor(response.data);
    } catch (error) {
      console.error("Failed to fetch live monitor:", error);
    }
  }, []);

  const fetchUsers = useCallback(async ({ showErrorToast = true } = {}) => {
    try {
      const response = await axios.get(`${API}/admin/users?limit=500`);
      setUsers(response.data.users);
      return response.data.users;
    } catch (error) {
      console.error("Failed to fetch users:", error);
      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar los usuarios" : "Failed to load users", language));
      }
      return [];
    }
  }, [language]);

  const fetchVideos = useCallback(async () => {
    try {
      setVideosLoading(true);
      const response = await axios.get(`${API}/admin/videos`);
      setVideos(response.data.videos);
    } catch (error) {
      console.error("Failed to fetch videos:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar los videos" : "Failed to load videos", language));
    } finally {
      setVideosLoading(false);
    }
  }, [language]);

  const handleAdminConsoleCleanup = useCallback(async () => {
    try {
      setConsoleCleanupLoading(true);
      const response = await axios.post(`${API}/admin/console/cleanup`);
      await Promise.allSettled([
        fetchStats({ showErrorToast: false }),
        fetchCapacity(),
        fetchR2Status(),
        fetchLiveMonitor(),
        fetchVideos(),
      ]);
      toast.success(
        language === "es"
          ? `Consola limpia. Fantasmas eliminados: ${response.data.cleaned_orphan_videos}. Caché limpiada: ${response.data.cleared_cache_entries}.`
          : `Console cleaned. Ghosts removed: ${response.data.cleaned_orphan_videos}. Cache entries cleared: ${response.data.cleared_cache_entries}.`
      );
    } catch (error) {
      console.error("Failed to clean admin console:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos limpiar la consola" : "Failed to clean admin console", language));
    } finally {
      setConsoleCleanupLoading(false);
    }
  }, [fetchCapacity, fetchLiveMonitor, fetchR2Status, fetchStats, fetchVideos, language]);

  const handleCleanupSingleGhostVideo = useCallback(async (videoId) => {
    try {
      setVideoCleanupLoadingId(videoId);
      await axios.post(`${API}/admin/videos/${videoId}/cleanup-orphan`);
      await Promise.allSettled([
        fetchVideos(),
        fetchR2Status(),
        fetchLiveMonitor(),
      ]);
      toast.success(language === "es" ? "Video fantasma eliminado" : "Ghost video removed");
    } catch (error) {
      console.error("Failed to cleanup orphan video:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos limpiar ese video" : "Failed to clean that video", language));
    } finally {
      setVideoCleanupLoadingId(null);
    }
  }, [fetchLiveMonitor, fetchR2Status, fetchVideos, language]);

  const fetchIssueReports = useCallback(async ({ showErrorToast = true } = {}) => {
    try {
      setReportsLoading(true);
      const response = await videoApi.getAdminIssueReports();
      setIssueReports(response.reports || []);
      setReportsTotal(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch issue reports:", error);
      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar los reportes" : "Failed to load issue reports", language));
      }
    } finally {
      setReportsLoading(false);
    }
  }, [language]);

  const deleteIssueReport = useCallback(async (reportId) => {
    await videoApi.deleteAdminIssueReport(reportId);
    setIssueReports((prev) => prev.filter((report) => report.report_id !== reportId));
    setReportsTotal((prev) => Math.max(0, prev - 1));
    toast.success(language === "es" ? "Reporte eliminado" : "Issue report deleted");
  }, [language]);

  const fetchReviews = useCallback(async ({ showErrorToast = true } = {}) => {
    try {
      setReviewsLoading(true);
      const response = await videoApi.getAdminReviews();
      setReviews(response.reviews || []);
      setReviewsTotal(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar los reviews" : "Failed to load reviews", language));
      }
    } finally {
      setReviewsLoading(false);
    }
  }, [language]);

  const deleteReview = useCallback(async (reviewId) => {
    await videoApi.deleteAdminReview(reviewId);
    setReviews((prev) => prev.filter((review) => review.review_id !== reviewId));
    setReviewsTotal((prev) => Math.max(0, prev - 1));
    toast.success(language === "es" ? "Review eliminada" : "Review deleted");
  }, [language]);

  const fetchErrorLogs = useCallback(async ({ showErrorToast = true } = {}) => {
    try {
      setErrorLogsLoading(true);
      const response = await videoApi.getAdminErrorLogs({ limit: 200 });
      setErrorLogs(response.logs || []);
      setErrorLogsTotal(response.total || 0);
      setErrorLogsUnseen(response.unseen_total || 0);
    } catch (error) {
      console.error("Failed to fetch error logs:", error);
      if (showErrorToast) {
        toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar el log de errores" : "Failed to load error logs", language));
      }
    } finally {
      setErrorLogsLoading(false);
    }
  }, [language]);

  const markErrorLogsSeen = useCallback(async (logIds = [], markAll = false) => {
    const response = await videoApi.markAdminErrorLogsSeen({ log_ids: logIds, mark_all: markAll });
    if ((response?.updated_count || 0) > 0) {
      await fetchErrorLogs();
    }
  }, [fetchErrorLogs]);

  const deleteSelectedErrorLogs = useCallback(async (logIds = []) => {
    if (!logIds.length) return;

    try {
      setDeletingErrorLogs(true);
      const response = await videoApi.deleteAdminErrorLogs({ log_ids: logIds });
      await fetchErrorLogs();
      toast.success(
        language === "es"
          ? `${response?.deleted_count || logIds.length} errores eliminados`
          : `${response?.deleted_count || logIds.length} error logs deleted`
      );
    } catch (error) {
      console.error("Failed to delete selected error logs:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos eliminar los errores seleccionados" : "Failed to delete selected error logs", language));
    } finally {
      setDeletingErrorLogs(false);
    }
  }, [fetchErrorLogs, language]);

  const handleManualRefresh = useCallback(async () => {
    setManualRefreshing(true);
    try {
      await Promise.allSettled([
        fetchStats(),
        fetchCosts(),
        fetchCapacity(),
        fetchR2Status(),
        fetchLiveMonitor(),
        activeTab === "users" ? fetchUsers() : Promise.resolve(),
        activeTab === "reports" ? fetchIssueReports() : Promise.resolve(),
        activeTab === "reviews" ? fetchReviews() : Promise.resolve(),
        activeTab === "error-logs" ? fetchErrorLogs() : Promise.resolve(),
        activeTab === "videos" ? fetchVideos() : Promise.resolve(),
      ]);
      toast.success(language === "es" ? "Consola actualizada" : "Admin console refreshed");
    } finally {
      setManualRefreshing(false);
    }
  }, [
    activeTab,
    fetchCapacity,
    fetchCosts,
    fetchIssueReports,
    fetchReviews,
    fetchErrorLogs,
    fetchLiveMonitor,
    fetchR2Status,
    fetchStats,
    fetchUsers,
    fetchVideos,
    language,
  ]);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/dashboard");
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.allSettled([
        fetchStats({ showErrorToast: false }),
        fetchCosts(),
        fetchCapacity(),
        fetchR2Status(),
        fetchLiveMonitor(),
      ]);
      setLoading(false);
    };

    loadData();
  }, [isAdmin, navigate, fetchStats, fetchCosts, fetchCapacity, fetchR2Status, fetchLiveMonitor, fetchUsers, fetchIssueReports, fetchReviews, fetchErrorLogs]);

  useEffect(() => {
    if (!isAdmin || activeTab !== "users" || users.length > 0) {
      return;
    }

    fetchUsers({ showErrorToast: false });
  }, [activeTab, fetchUsers, isAdmin, users.length]);

  useEffect(() => {
    if (!isAdmin || activeTab !== "videos" || videos.length > 0 || videosLoading) {
      return;
    }

    fetchVideos();
  }, [activeTab, isAdmin, videos.length, videosLoading, fetchVideos]);

  useEffect(() => {
    if (!isAdmin || activeTab !== "reports" || reportsLoading || issueReports.length > 0) {
      return;
    }

    fetchIssueReports({ showErrorToast: false });
  }, [activeTab, fetchIssueReports, isAdmin, issueReports.length, reportsLoading]);

  useEffect(() => {
    if (!isAdmin || activeTab !== "reviews" || reviewsLoading || reviews.length > 0) {
      return;
    }

    fetchReviews({ showErrorToast: false });
  }, [activeTab, fetchReviews, isAdmin, reviews.length, reviewsLoading]);

  useEffect(() => {
    if (!isAdmin || activeTab !== "error-logs") return;
    if (!errorLogs.length && !errorLogsLoading) {
      fetchErrorLogs({ showErrorToast: false });
    }
  }, [activeTab, errorLogs.length, errorLogsLoading, fetchErrorLogs, isAdmin]);

  // Subscriptions the customer is paying, as Mercado Pago has them right now. Deleting the account or setting
  // its plan by hand cancels them, so the admin sees them before confirming. An unfinished checkout is left
  // out: nobody is being charged for it.
  const fetchPaidSubscriptions = async (userId) => {
    const response = await axios.get(`${API}/admin/users/${userId}/chargeable-subscriptions`);
    return (response.data?.subscriptions || []).filter((subscription) => subscription.status !== "pending");
  };

  const describeSubscriptions = (subscriptions) => subscriptions
    .map((subscription) => {
      const name = subscription.reason || (language === "es" ? "suscripción" : "subscription");
      const paused = subscription.status === "paused" ? (language === "es" ? ", en pausa" : ", paused") : "";
      if (typeof subscription.amount !== "number") return `${name}${paused}`;
      const amount = `${new Intl.NumberFormat("es-CO").format(subscription.amount)} ${subscription.currency || ""}`.trim();
      return language === "es" ? `${name}, ${amount} al mes${paused}` : `${name}, ${amount} a month${paused}`;
    })
    .join("; ");

  // An unfinished checkout that gets cancelled along the way was never charging anybody.
  const stoppedACharge = (response) => (response.data?.cancelled_subscriptions || [])
    .some((subscription) => subscription.previous_status !== "pending");

  const handleMembershipChange = async (userId, newMembership, confirmSubscriptionCancellation = false) => {
    try {
      setChangingMembership(true);
      const response = await axios.put(`${API}/admin/users/${userId}/membership`, {
        membership: newMembership,
        confirm_subscription_cancellation: confirmSubscriptionCancellation,
      });
      if (stoppedACharge(response)) {
        toast.success(language === "es"
          ? `Plan actualizado a ${newMembership}. Se canceló su suscripción en Mercado Pago: ya no se le cobrará.`
          : `Membership updated to ${newMembership}. Their Mercado Pago subscription was cancelled: they will not be charged again.`);
      } else if (response.data?.external_subscription_requires_manual_cancellation) {
        toast.warning(language === "es"
          ? `Plan actualizado a ${newMembership}. Su suscripción anterior sigue activa en el proveedor de pagos: cancélala allá.`
          : `Membership updated to ${newMembership}. Their previous subscription is still active with the payment provider: cancel it there.`);
      } else {
        toast.success(language === "es" ? `Plan actualizado a ${newMembership}` : `Membership updated to ${newMembership}`);
      }
      setPendingMembershipChange(null);
      await Promise.all([fetchUsers(), fetchStats()]);
      if (selectedUser?.user?.id === userId) {
        await viewUserDetails(userId);
      }
    } catch (error) {
      console.error("Failed to update membership:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos actualizar la membresía" : "Failed to update membership", language));
    } finally {
      setChangingMembership(false);
    }
  };

  const requestMembershipChange = async (user, newMembership) => {
    let paidSubscriptions;
    try {
      paidSubscriptions = await fetchPaidSubscriptions(user.id);
    } catch (error) {
      console.error("Failed to check subscriptions:", error);
      toast.error(getApiErrorMessage(
        error,
        language === "es" ? "No pudimos consultar sus suscripciones en Mercado Pago. El plan no se cambió." : "Could not check their Mercado Pago subscriptions. The plan was not changed.",
        language
      ));
      return;
    }
    if (paidSubscriptions.length > 0) {
      setPendingMembershipChange({ user, membership: newMembership, subscriptions: paidSubscriptions });
      return;
    }
    await handleMembershipChange(user.id, newMembership);
  };

  const openDeleteDialog = async (user) => {
    deleteDialogUserIdRef.current = user.id;
    setUserToDelete(user);
    setUserToDeleteSubscriptions(null);
    setDeleteDialogOpen(true);
    let paidSubscriptions = [];
    try {
      paidSubscriptions = await fetchPaidSubscriptions(user.id);
    } catch (error) {
      // The server looks again when the account is deleted, and refuses rather than cancel unannounced.
      console.error("Failed to check subscriptions:", error);
      toast.error(getApiErrorMessage(
        error,
        language === "es" ? "No pudimos consultar sus suscripciones en Mercado Pago" : "Could not check their Mercado Pago subscriptions",
        language
      ));
    }
    if (deleteDialogUserIdRef.current === user.id) {
      setUserToDeleteSubscriptions(paidSubscriptions);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete || deletingUser) return;

    try {
      setDeletingUser(true);
      const response = await axios.delete(`${API}/admin/users/${userToDelete.id}`, {
        params: { confirm_subscription_cancellation: (userToDeleteSubscriptions || []).length > 0 },
      });
      const refreshedUsers = await fetchUsers();
      await fetchStats();

      const stillExists = refreshedUsers.some((user) => user.id === userToDelete.id || user.email === userToDelete.email);
      if (response.data?.verified !== true || stillExists) {
        throw new Error(language === "es" ? "No pudimos verificar el borrado del usuario" : "Could not verify user deletion");
      }

      if (selectedUser?.user?.id === userToDelete.id) {
        setSelectedUser(null);
      }

      if (stoppedACharge(response)) {
        toast.success(language === "es"
          ? "Usuario eliminado. Se canceló su suscripción en Mercado Pago: ya no se le cobrará."
          : "User deleted. Their Mercado Pago subscription was cancelled: they will not be charged again.");
      } else {
        toast.success(language === "es" ? "Usuario eliminado correctamente" : "User deleted successfully");
      }
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Failed to delete user:", error);
      logActionError({
        action: "admin_delete_user",
        error,
        title: language === "es" ? "Error al eliminar usuario" : "Delete user failed",
        context: {
          adminTab: activeTab,
          deletingUserId: userToDelete?.id || null,
          deletingUserEmail: userToDelete?.email || null,
          deletingUsername: userToDelete?.username || null,
        },
      });
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos eliminar el usuario" : "Failed to delete user", language));
    } finally {
      setDeletingUser(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`);
      setSelectedUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar el detalle del usuario" : "Failed to load user details", language));
    }
  };

  const [downloadingDianReport, setDownloadingDianReport] = useState(false);

  const handleDownloadDianReport = async () => {
    setDownloadingDianReport(true);
    try {
      const response = await axios.get(`${API}/admin/billing/dian-export`, { responseType: "blob" });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `voxara_dian_export_${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download DIAN report:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos generar el reporte" : "Failed to generate the report", language));
    } finally {
      setDownloadingDianReport(false);
    }
  };

  const handleResetAllUsage = async () => {
    if (!window.confirm(language === "es" 
      ? "¿Estás seguro de resetear el uso diario de TODOS los usuarios?" 
      : "Are you sure you want to reset daily usage for ALL users?")) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/reset-all-usage`);
      toast.success(language === "es" 
        ? `Uso reseteado para ${response.data.deleted_count} usuarios` 
        : `Usage reset for ${response.data.deleted_count} users`);
      fetchStats();
      fetchUsers();
      if (selectedUserData?.id) {
        viewUserDetails(selectedUserData.id);
      }
    } catch (error) {
      console.error("Failed to reset usage:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "Error al resetear uso" : "Failed to reset usage", language));
    }
  };

  const handleResetUserUsage = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-usage`);
      toast.success(language === "es" 
        ? `Uso reseteado para ${email}` 
        : `Usage reset for ${email}`);
      fetchUsers();
      if (selectedUser?.user?.id === userId) {
        viewUserDetails(userId);
      }
    } catch (error) {
      console.error("Failed to reset user usage:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "Error al resetear uso" : "Failed to reset usage", language));
    }
  };

  const formatUsageTime = (minutes, isUnlimited = false) => {
    if (isUnlimited) {
      return language === "es" ? "Ilimitado" : "Unlimited";
    }

    const totalMinutes = Number(minutes || 0);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);

    if (hrs === 0) {
      return `${mins}m`;
    }

    return `${hrs}h ${mins}m`;
  };

  const getUsageTitle = (usageInfo) => {
    if (!usageInfo) {
      return language === "es" ? "Uso diario" : "Daily usage";
    }

    if (usageInfo.is_admin) {
      return language === "es" ? "Uso del plan" : "Plan usage";
    }

    return usageInfo.lifetime_limit
      ? (language === "es" ? "Uso del plan free" : "Free plan usage")
      : (language === "es" ? "Uso diario" : "Daily usage");
  };

  const getUsageSubtitle = (usageInfo) => {
    if (!usageInfo) {
      return language === "es" ? "Restante del día actual" : "Remaining for the current day";
    }

    if (usageInfo.is_admin) {
      return language === "es" ? "Sin límite diario" : "No daily limit";
    }

    return usageInfo.lifetime_limit
      ? (language === "es" ? "Límite vitalicio del plan free" : "Lifetime free-tier limit")
      : (language === "es" ? "Restante del día actual" : "Remaining for the current day");
  };

  const getMembershipBadgeClass = (membership) => {
    switch (membership) {
      case "enterprise":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "professional":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "starter":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const normalizedUserSearch = userSearch.trim().toLowerCase();
  const filteredUsers = users.filter((user) => {
    if (!normalizedUserSearch) return true;
    return (
      user.username?.toLowerCase().includes(normalizedUserSearch) ||
      user.email?.toLowerCase().includes(normalizedUserSearch)
    );
  });

  const userGroups = [
    {
      key: "admin",
      label: language === "es" ? "Admins" : "Admins",
      users: filteredUsers.filter((user) => user.is_admin),
    },
    {
      key: "enterprise",
      label: "Enterprise",
      users: filteredUsers.filter((user) => !user.is_admin && (user.membership || "free") === "enterprise"),
    },
    {
      key: "professional",
      label: "Professional",
      users: filteredUsers.filter((user) => !user.is_admin && (user.membership || "free") === "professional"),
    },
    {
      key: "starter",
      label: "Starter",
      users: filteredUsers.filter((user) => !user.is_admin && (user.membership || "free") === "starter"),
    },
    {
      key: "free",
      label: "Free",
      users: filteredUsers.filter((user) => !user.is_admin && (user.membership || "free") === "free"),
    },
  ].filter((group) => group.users.length > 0);

  const renderUserRows = (groupUsers, groupKey) => (
    groupUsers.map((user) => (
      <TableRow key={user.id} data-testid={`admin-user-row-${user.id}`}>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="font-medium">{user.username}</span>
            {user.is_admin && (
              <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">
                Admin
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-muted-foreground">{user.email}</TableCell>
        <TableCell>
          <Select
            value={user.membership || "free"}
            onValueChange={(value) => requestMembershipChange(user, value)}
            disabled={user.is_admin}
          >
            <SelectTrigger className={`w-32 ${getMembershipBadgeClass(user.membership)}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        {groupKey === "free" && (
          <TableCell data-testid={`admin-user-total-duration-${user.id}`}>
            {user.total_video_duration ? formatDuration(user.total_video_duration) : "-"}
          </TableCell>
        )}
        <TableCell>{user.video_count}</TableCell>
        <TableCell className="text-muted-foreground text-sm">
          {formatDate(user.created_at)}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => viewUserDetails(user.id)}
              title={language === "es" ? "Ver detalles" : "View details"}
              data-testid={`view-user-${user.id}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
            {!user.is_admin && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleResetUserUsage(user.id, user.email)}
                  title={language === "es" ? "Resetear uso diario" : "Reset daily usage"}
                  className="text-orange-400 hover:text-orange-300 hover:bg-orange-400/10"
                  data-testid={`reset-usage-${user.id}`}
                >
                  <Clock className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openDeleteDialog(user)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
    ))
  );

  const renderMobileUserCards = (groupUsers, groupKey) => (
    groupUsers.map((user) => (
      <div key={user.id} className="rounded-xl border border-border bg-background p-4 space-y-3" data-testid={`admin-user-card-${user.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold break-words">{user.username}</p>
              {user.is_admin && <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">Admin</Badge>}
            </div>
            <p className="text-sm text-muted-foreground break-all">{user.email}</p>
          </div>
          <Badge variant="outline" className="capitalize shrink-0">{user.membership || "free"}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">{language === "es" ? "Videos únicos" : "Unique videos"}</p>
            <p className="font-medium">{user.video_count}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Joined</p>
            <p className="font-medium">{formatDate(user.created_at)}</p>
          </div>
          {groupKey === "free" && (
            <div className="col-span-2">
              <p className="text-xs text-muted-foreground">{language === "es" ? "Duración subida" : "Uploaded duration"}</p>
              <p className="font-medium" data-testid={`admin-user-total-duration-${user.id}`}>{user.total_video_duration ? formatDuration(user.total_video_duration) : "-"}</p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={() => viewUserDetails(user.id)} data-testid={`mobile-view-user-${user.id}`}>
            <Eye className="w-4 h-4 mr-2" />
            {language === "es" ? "Ver" : "View"}
          </Button>
          {!user.is_admin ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleResetUserUsage(user.id, user.email)}
                className="text-orange-400 hover:text-orange-300 hover:bg-orange-400/10"
                data-testid={`mobile-reset-usage-${user.id}`}
              >
                <Clock className="w-4 h-4 mr-2" />
                {language === "es" ? "Reset uso" : "Reset usage"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="col-span-2"
                onClick={() => openDeleteDialog(user)}
                data-testid={`mobile-delete-user-${user.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {language === "es" ? "Eliminar usuario" : "Delete user"}
              </Button>
            </>
          ) : (
            <div className="col-span-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              {language === "es" ? "Los admins no se pueden borrar desde aquí." : "Admins cannot be deleted here."}
            </div>
          )}
        </div>
      </div>
    ))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto max-w-7xl px-4 pt-24 pb-12 sm:px-6">
          <Card className="bg-card border-border p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Header />

      <main className="container mx-auto max-w-7xl px-4 pt-24 pb-12 sm:px-6">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Admin Dashboard</h1>
              <p className="text-muted-foreground">Manage users, memberships, and videos</p>
            </div>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              variant="outline"
              onClick={handleAdminConsoleCleanup}
              disabled={consoleCleanupLoading}
              className="w-full sm:w-auto"
              data-testid="admin-console-cleanup-button"
            >
              {consoleCleanupLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              {consoleCleanupLoading
                ? (language === "es" ? "Limpiando..." : "Cleaning...")
                : (language === "es" ? "Limpiar consola" : "Clean console")}
            </Button>
            <Button
              variant="outline"
              onClick={handleManualRefresh}
              disabled={manualRefreshing}
              className="w-full sm:w-auto"
              data-testid="refresh-admin-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${manualRefreshing ? "animate-spin" : ""}`} />
              {manualRefreshing
                ? (language === "es" ? "Actualizando..." : "Refreshing...")
                : (language === "es" ? "Actualizar" : "Refresh")}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 md:grid-cols-5">
            <Card className="bg-card border-border p-4" data-testid="admin-stats-total-users-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_users}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
              </div>
            </Card>
            <Card className="bg-card border-border p-4" data-testid="admin-stats-total-videos-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-purple-500/20 flex items-center justify-center">
                  <Video className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_videos}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === "es" ? "Videos únicos completados" : "Unique completed videos"}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-card border-border p-4" data-testid="admin-stats-total-completed-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_completed}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === "es" ? "Registros completados" : "Completed records"}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="bg-card border-border p-4" data-testid="admin-stats-total-processing-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_processing}</p>
                  <p className="text-xs text-muted-foreground">Processing</p>
                </div>
              </div>
            </Card>
            <Card className="bg-card border-border p-4" data-testid="admin-stats-total-failed-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-red-500/20 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_failed}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {stats?.growth && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Card className="bg-card border-border p-4" data-testid="admin-stats-new-users-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-cyan-500/20 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="space-y-2">
                  <p className="text-2xl font-bold" data-testid="admin-stats-new-users-30d-value">{stats.growth.new_users_30d}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === "es" ? "Nuevos usuarios verificados (30 días)" : "New verified users (30 days)"}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="admin-stats-new-users-7d-value">
                    {language === "es" ? `Últimos 7 días: ${stats.growth.new_users_7d}` : `Last 7 days: ${stats.growth.new_users_7d}`}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border p-4" data-testid="admin-stats-memberships-purchased-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="space-y-2">
                  <p className="text-2xl font-bold" data-testid="admin-stats-memberships-purchased-total-value">{stats.growth.memberships_purchased_total}</p>
                  <p className="text-xs text-muted-foreground">
                    {language === "es" ? "Membresías compradas" : "Memberships purchased"}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="admin-stats-memberships-purchased-30d-value">
                    {language === "es" ? `Últimos 30 días: ${stats.growth.memberships_purchased_30d}` : `Last 30 days: ${stats.growth.memberships_purchased_30d}`}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Membership Stats */}
        {stats && (
          <Card className="bg-card border-border p-4 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Crown className="w-4 h-4 text-primary" />
              Membership Distribution
            </h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="text-center p-3 rounded bg-gray-500/10">
                <p className="text-2xl font-bold">{stats.memberships.free}</p>
                <p className="text-xs text-muted-foreground">Free</p>
              </div>
              <div className="text-center p-3 rounded bg-green-500/10">
                <p className="text-2xl font-bold">{stats.memberships.starter}</p>
                <p className="text-xs text-muted-foreground">Starter</p>
              </div>
              <div className="text-center p-3 rounded bg-blue-500/10">
                <p className="text-2xl font-bold">{stats.memberships.pro}</p>
                <p className="text-xs text-muted-foreground">Pro</p>
              </div>
              <div className="text-center p-3 rounded bg-purple-500/10">
                <p className="text-2xl font-bold">{stats.memberships.enterprise}</p>
                <p className="text-xs text-muted-foreground">Enterprise</p>
              </div>
            </div>
          </Card>
        )}

        {/* Groq API Costs Panel */}
        {costs && (
          <Card className="bg-card border-border p-4 mb-8">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              {language === "es" ? "Costos de API Groq" : "Groq API Costs"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Today */}
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs text-blue-400 mb-1">{language === "es" ? "Hoy" : "Today"}</p>
                <p className="text-2xl font-bold text-blue-400">${costs.today.estimated_cost}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {costs.today.hours.toFixed(1)}h ({costs.today.minutes.toFixed(0)} min)
                </p>
              </div>
              {/* This Month */}
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-xs text-green-400 mb-1">{language === "es" ? "Este Mes" : "This Month"}</p>
                <p className="text-2xl font-bold text-green-400">${costs.this_month.estimated_cost}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {costs.this_month.hours.toFixed(1)}h ({costs.this_month.minutes.toFixed(0)} min)
                </p>
              </div>
              {/* All Time */}
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-xs text-purple-400 mb-1">{language === "es" ? "Total Histórico" : "All Time"}</p>
                <p className="text-2xl font-bold text-purple-400">${costs.all_time.estimated_cost}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {costs.all_time.hours.toFixed(1)}h ({costs.all_time.minutes.toFixed(0)} min)
                </p>
              </div>
            </div>
            
            {/* Pricing Info */}
            <div className="p-3 rounded bg-muted/30 mb-4">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">{language === "es" ? "Precios Groq:" : "Groq Pricing:"}</span>{" "}
                Whisper ${costs.pricing.whisper_per_hour}/hr + Llama ~${costs.pricing.llama_per_hour}/hr ={" "}
                <span className="text-primary font-medium">${costs.pricing.total_per_hour}/hr</span>
              </p>
            </div>

            {/* Top Users */}
            {costs.top_users_this_month && costs.top_users_this_month.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  {language === "es" ? "Usuarios con Mayor Uso (Este Mes)" : "Top Users This Month"}
                </h4>
                <div className="space-y-2">
                  {costs.top_users_this_month.slice(0, 5).map((user, idx) => (
                    <div key={user.user_id} className="flex flex-col gap-3 rounded bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="text-xs text-muted-foreground w-4">#{idx + 1}</span>
                        <span className="min-w-0 break-all text-sm">{user.email}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 sm:justify-end">
                        <span className="text-xs text-muted-foreground">
                          {(user.minutes_used / 60).toFixed(1)}h
                        </span>
                        <Badge variant="outline" className="text-green-400 border-green-400/30">
                          ${user.estimated_cost}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* System Capacity Panel - LIVE */}
        {capacity && (
          <Card className="bg-card border-border p-4 mb-8" data-testid="system-capacity-panel">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Server className="w-4 h-4 text-cyan-400 animate-pulse" />
              {language === "es" ? "Capacidad del Sistema" : "System Capacity"}
              <span className="ml-auto text-xs text-green-400 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                LIVE
              </span>
            </h3>
            
            {/* Resources Grid with Animated Gauges */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 mb-6">
              {/* CPU Gauge */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs font-medium">CPU</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{capacity.resources.cpu.cores} cores</span>
                </div>
                <p className="text-3xl font-bold tabular-nums transition-all duration-300">
                  {capacity.resources.cpu.usage_percent}%
                </p>
                <div className="w-full bg-muted/30 rounded-full h-2 mt-2 overflow-hidden">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      capacity.resources.cpu.usage_percent > 80 ? 'bg-red-500' :
                      capacity.resources.cpu.usage_percent > 50 ? 'bg-yellow-500' : 'bg-cyan-500'
                    }`}
                    style={{ width: `${capacity.resources.cpu.usage_percent}%` }}
                  />
                </div>
              </div>
              
              {/* RAM Gauge */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-purple-400" />
                    <span className="text-xs font-medium">RAM</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{capacity.resources.memory.total_gb}GB</span>
                </div>
                <p className="text-3xl font-bold tabular-nums transition-all duration-300">
                  {capacity.resources.memory.usage_percent}%
                </p>
                <div className="w-full bg-muted/30 rounded-full h-2 mt-2 overflow-hidden">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      capacity.resources.memory.usage_percent > 85 ? 'bg-red-500' :
                      capacity.resources.memory.usage_percent > 70 ? 'bg-yellow-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${capacity.resources.memory.usage_percent}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{capacity.resources.memory.available_gb}GB libre</p>
              </div>
              
              {/* Disk /app Gauge */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-orange-400" />
                    <span className="text-xs font-medium">/app</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{capacity.resources.disk_app.total_gb}GB</span>
                </div>
                <p className="text-3xl font-bold tabular-nums transition-all duration-300">
                  {capacity.resources.disk_app.usage_percent}%
                </p>
                <div className="w-full bg-muted/30 rounded-full h-2 mt-2 overflow-hidden">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      capacity.resources.disk_app.usage_percent > 90 ? 'bg-red-500 animate-pulse' :
                      capacity.resources.disk_app.usage_percent > 80 ? 'bg-yellow-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${capacity.resources.disk_app.usage_percent}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{capacity.resources.disk_app.free_gb}GB libre</p>
              </div>
              
              {/* Disk Processing Gauge */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-blue-400" />
                    <span className="text-xs font-medium">/tmp</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{capacity.resources.disk_processing.total_gb}GB</span>
                </div>
                <p className="text-3xl font-bold tabular-nums transition-all duration-300">
                  {capacity.resources.disk_processing.usage_percent}%
                </p>
                <div className="w-full bg-muted/30 rounded-full h-2 mt-2 overflow-hidden">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      capacity.resources.disk_processing.usage_percent > 85 ? 'bg-red-500' :
                      capacity.resources.disk_processing.usage_percent > 60 ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${capacity.resources.disk_processing.usage_percent}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{capacity.resources.disk_processing.free_gb}GB libre</p>
              </div>
            </div>

            {/* Live Processing Status */}
            <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
              {/* Workers Status - Big Display */}
              <div className="p-4 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 col-span-1 md:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <Gauge className="w-5 h-5 text-green-400" />
                  <span className="font-medium">{language === "es" ? "Procesamiento en Vivo" : "Live Processing"}</span>
                </div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                  <div>
                    <p className="text-5xl font-bold tabular-nums text-green-400 transition-all duration-300">
                      {capacity.current_load.processing}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      / {capacity.capacity.max_workers} workers
                    </p>
                  </div>
                  <div className="flex-1 grid grid-cols-5 gap-1 sm:grid-cols-10">
                    {[...Array(capacity.capacity.max_workers)].map((_, i) => (
                      <div 
                        key={i}
                        className={`h-8 rounded transition-all duration-300 ${
                          i < capacity.current_load.processing 
                            ? 'bg-green-500 animate-pulse' 
                            : 'bg-muted/30'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span>{language === "es" ? "En cola" : "Queue"}: {capacity.current_load.pending}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span>{language === "es" ? "Subiendo" : "Uploading"}: {capacity.current_load.uploading}</span>
                  </div>
                  {capacity.current_load.breakdown && (
                    <>
                      {capacity.current_load.breakdown.extracting_audio > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-orange-500 animate-pulse"></div>
                          <span>{language === "es" ? "Extrayendo" : "Extracting"}: {capacity.current_load.breakdown.extracting_audio}</span>
                        </div>
                      )}
                      {capacity.current_load.breakdown.transcribing > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-purple-500 animate-pulse"></div>
                          <span>{language === "es" ? "Transcribiendo" : "Transcribing"}: {capacity.current_load.breakdown.transcribing}</span>
                        </div>
                      )}
                      {capacity.current_load.breakdown.analyzing > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-cyan-500 animate-pulse"></div>
                          <span>{language === "es" ? "Analizando" : "Analyzing"}: {capacity.current_load.breakdown.analyzing}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Throughput */}
              <div className="p-4 rounded-lg bg-muted/20 border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">{language === "es" ? "Rendimiento" : "Throughput"}</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-2xl font-bold tabular-nums">{capacity.performance.throughput_videos_per_hour}</p>
                    <p className="text-xs text-muted-foreground">{language === "es" ? "videos/hora máx" : "videos/hour max"}</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold">~{capacity.performance.max_concurrent_users}</p>
                    <p className="text-xs text-muted-foreground">{language === "es" ? "usuarios simultáneos" : "concurrent users"}</p>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <div className="text-xs flex items-center flex-wrap gap-1">
                      <span className="text-muted-foreground">{language === "es" ? "Cuello de botella" : "Bottleneck"}:</span>
                      <Badge variant="outline" className="text-cyan-400 border-cyan-400/30">
                        {capacity.capacity.bottleneck}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            {capacity.recommendations && capacity.recommendations.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  {language === "es" ? "Estado" : "Status"}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {capacity.recommendations.map((rec, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline"
                      className={`${
                        rec.priority === 'critical' ? 'text-red-400 border-red-400/30 bg-red-500/10' :
                        rec.priority === 'warning' ? 'text-yellow-400 border-yellow-400/30 bg-yellow-500/10' :
                        rec.priority === 'info' ? 'text-blue-400 border-blue-400/30 bg-blue-500/10' :
                        'text-green-400 border-green-400/30 bg-green-500/10'
                      }`}
                    >
                      {rec.issue}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Cloudflare R2 Storage Panel */}
        {r2Status && r2Status.status === "connected" && (
          <Card className="bg-card border-border p-4 mb-8" data-testid="r2-storage-panel">
            <div className="flex items-center gap-2 mb-4">
              <HardDrive className="w-5 h-5 text-orange-400" />
              <h3 className="font-semibold text-lg">Cloudflare R2 Storage</h3>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs ml-auto">
                Connected
              </Badge>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 mb-4">
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Almacenamiento Total" : "Total Storage"}</p>
                <p className="text-2xl font-bold text-orange-400">{r2Status.storage.total_size_mb} MB</p>
                <p className="text-xs text-muted-foreground">{r2Status.storage.total_objects} {language === "es" ? "archivos" : "files"}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-xs text-muted-foreground mb-1">Videos</p>
                <p className="text-2xl font-bold text-blue-400">{r2Status.storage.by_type.videos.count}</p>
                <p className="text-xs text-muted-foreground">{r2Status.storage.by_type.videos.size_mb} MB</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-xs text-muted-foreground mb-1">Audio</p>
                <p className="text-2xl font-bold text-purple-400">{r2Status.storage.by_type.audio.count}</p>
                <p className="text-xs text-muted-foreground">{r2Status.storage.by_type.audio.size_mb} MB</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Costo Estimado" : "Estimated Cost"}</p>
                <p className="text-2xl font-bold text-green-400">${r2Status.billing.total_estimated_cost_usd}</p>
                <p className="text-xs text-muted-foreground">/mes</p>
              </div>
            </div>

            {/* Billing Details */}
            <div className="p-3 rounded-lg bg-muted/20 border border-border">
              <p className="text-sm font-medium mb-2">{language === "es" ? "Detalles de Facturación R2" : "R2 Billing Details"}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-muted-foreground">{language === "es" ? "Tier Gratis" : "Free Tier"}:</span>
                  <span className="ml-1 font-medium">{r2Status.billing.free_tier_gb} GB</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{language === "es" ? "Facturable" : "Billable"}:</span>
                  <span className="ml-1 font-medium">{r2Status.billing.billable_storage_gb} GB</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{language === "es" ? "Costo Storage" : "Storage Cost"}:</span>
                  <span className="ml-1 font-medium">${r2Status.billing.storage_cost_usd}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{language === "es" ? "Costo Ops" : "Ops Cost"}:</span>
                  <span className="ml-1 font-medium">${r2Status.billing.estimated_ops_cost_usd}</span>
                </div>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {language === "es" ? "Precios" : "Pricing"}: Storage {r2Status.billing.pricing.storage_per_gb}/GB | Egress: {r2Status.billing.pricing.egress}
              </div>
            </div>

            {r2Status.cleanup_hygiene && (
              <div className="mt-4 p-3 rounded-lg bg-muted/20 border border-border" data-testid="r2-cleanup-hygiene-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className={`w-4 h-4 ${r2Status.cleanup_hygiene.currently_clean ? "text-green-400" : "text-yellow-400"}`} />
                  <p className="text-sm font-medium">{language === "es" ? "Higiene de Storage" : "Storage Hygiene"}</p>
                  <Badge
                    className={`${r2Status.cleanup_hygiene.currently_clean ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"} ml-auto text-xs`}
                    data-testid="r2-clean-status-badge"
                  >
                    {r2Status.cleanup_hygiene.currently_clean
                      ? (language === "es" ? "R2 limpio" : "R2 clean")
                      : (language === "es" ? "Revisar" : "Attention")}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20" data-testid="r2-clean-card">
                    <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "R2 Clean" : "R2 Clean"}</p>
                    <p className="text-2xl font-bold text-green-400">{r2Status.cleanup_hygiene.currently_clean ? "Yes" : "No"}</p>
                    <p className="text-xs text-muted-foreground">{language === "es" ? "Objetos esperados" : "Expected objects"}: {r2Status.cleanup_hygiene.expected_active_objects}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="r2-orphan-deleted-card">
                    <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Orphans Deleted" : "Orphans Deleted"}</p>
                    <p className="text-2xl font-bold text-red-400">{r2Status.cleanup_hygiene.total_deleted_orphans}</p>
                    <p className="text-xs text-muted-foreground">{language === "es" ? "Última pasada" : "Last pass"}: {r2Status.cleanup_hygiene.last_deleted_orphans}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20" data-testid="r2-stale-refs-card">
                    <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Stale Refs Cleared" : "Stale Refs Cleared"}</p>
                    <p className="text-2xl font-bold text-yellow-400">{r2Status.cleanup_hygiene.total_cleared_stale_refs}</p>
                    <p className="text-xs text-muted-foreground">{language === "es" ? "Última pasada" : "Last pass"}: {r2Status.cleanup_hygiene.last_cleared_stale_refs}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20" data-testid="r2-last-reconcile-card">
                    <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Última Reconciliación" : "Last Reconcile"}</p>
                    <p className="text-sm font-semibold text-cyan-400">
                      {r2Status.cleanup_hygiene.last_run_at
                        ? new Date(r2Status.cleanup_hygiene.last_run_at).toLocaleTimeString(language === "es" ? "es-ES" : "en-US")
                        : "—"}
                    </p>
                    <p className="text-xs text-muted-foreground">{language === "es" ? "Active keys (última pasada)" : "Active keys (last pass)"}: {r2Status.cleanup_hygiene.active_keys_last_reconcile}</p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Live Activity Monitor */}
        {liveMonitor && (
          <Card className="bg-card border-border p-4 mb-8" data-testid="live-monitor-panel">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-green-400 animate-pulse" />
              <h3 className="font-semibold text-lg">{language === "es" ? "Actividad en Tiempo Real" : "Live Activity"}</h3>
              <span className="text-xs text-muted-foreground ml-auto">
                {new Date(liveMonitor.timestamp).toLocaleTimeString()}
              </span>
            </div>

            {/* Today's Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Videos Hoy" : "Videos Today"}</p>
                <p className="text-2xl font-bold text-cyan-400">{liveMonitor.today.videos_uploaded}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Completados Hoy" : "Completed Today"}</p>
                <p className="text-2xl font-bold text-green-400">{liveMonitor.today.videos_completed}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Videos Este Mes" : "Videos This Month"}</p>
                <p className="text-2xl font-bold text-purple-400">{liveMonitor.this_month.videos_total}</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <p className="text-xs text-muted-foreground mb-1">{language === "es" ? "Groq API Calls (est.)" : "Groq API Calls (est.)"}</p>
                <p className="text-2xl font-bold text-orange-400">{liveMonitor.this_month.groq_api_calls_estimate}</p>
              </div>
            </div>

            {liveMonitor.queue_balance && (
              <div className="mb-4 space-y-4" data-testid="queue-balance-panel">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Server className="w-4 h-4 text-cyan-400" />
                    {language === "es" ? "Balance entre nodos" : "Worker Balance"}
                  </p>
                  <span className="text-xs text-muted-foreground" data-testid="queue-balance-window">
                    {language === "es"
                      ? `Ventana reciente: ${liveMonitor.queue_balance.distribution_window || 0} jobs`
                      : `Recent window: ${liveMonitor.queue_balance.distribution_window || 0} jobs`}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {liveMonitor.queue_balance.recent_jobs_by_node?.map((node) => (
                    <div
                      key={node.node_id}
                      className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20"
                      data-testid={`queue-balance-node-${node.node_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-sm">{node.node_id}</p>
                          <p className="text-xs text-muted-foreground capitalize">{node.node_type}</p>
                        </div>
                        <Badge variant="outline" className="text-cyan-300 border-cyan-500/30">
                          {node.current_jobs}/{node.max_concurrent_jobs}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {language === "es" ? "Jobs recientes" : "Recent jobs"}
                          </p>
                          <p className="text-lg font-semibold text-cyan-300" data-testid={`queue-balance-node-recent-${node.node_id}`}>
                            {node.recent_jobs}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {language === "es" ? "Último heartbeat" : "Last heartbeat"}
                          </p>
                          <p className="text-sm font-medium text-foreground/90">
                            {node.last_heartbeat
                              ? new Date(node.last_heartbeat).toLocaleTimeString(language === "es" ? "es-ES" : "en-US")
                              : "—"}
                          </p>
                        </div>
                      </div>

                      {node.resources && (
                        <div className="mt-3 pt-3 border-t border-cyan-500/10 space-y-3" data-testid={`queue-balance-node-resources-${node.node_id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                              {language === "es" ? "Poder del nodo" : "Node power"}
                            </p>
                            <span className="text-[11px] text-muted-foreground font-mono">
                              {(node.server_code_hash || "—").slice(0, 12)}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="rounded-md bg-background/40 px-2 py-2 border border-border/40">
                              <p className="text-muted-foreground">CPU</p>
                              <p className="font-semibold text-cyan-300" data-testid={`queue-balance-node-cpu-${node.node_id}`}>
                                {node.resources.cpu?.usage_percent ?? "—"}% · {node.resources.cpu?.cores ?? "—"} cores
                              </p>
                            </div>
                            <div className="rounded-md bg-background/40 px-2 py-2 border border-border/40">
                              <p className="text-muted-foreground">RAM</p>
                              <p className="font-semibold text-purple-300" data-testid={`queue-balance-node-memory-${node.node_id}`}>
                                {node.resources.memory?.usage_percent ?? "—"}% · {node.resources.memory?.available_gb ?? "—"}GB libre
                              </p>
                            </div>
                            <div className="rounded-md bg-background/40 px-2 py-2 border border-border/40">
                              <p className="text-muted-foreground">App</p>
                              <p className="font-semibold text-orange-300" data-testid={`queue-balance-node-disk-app-${node.node_id}`}>
                                {node.resources.disk_app?.usage_percent ?? "—"}% · {node.resources.disk_app?.free_gb ?? "—"}GB libre
                              </p>
                            </div>
                            <div className="rounded-md bg-background/40 px-2 py-2 border border-border/40">
                              <p className="text-muted-foreground">Tmp</p>
                              <p className="font-semibold text-blue-300" data-testid={`queue-balance-node-disk-tmp-${node.node_id}`}>
                                {node.resources.disk_processing?.usage_percent ?? "—"}% · {node.resources.disk_processing?.free_gb ?? "—"}GB libre
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {liveMonitor.queue_balance.recent_claims?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      {language === "es" ? "Últimos jobs reclamados" : "Latest claimed jobs"}
                    </p>
                    <div className="space-y-2 max-h-44 overflow-y-auto">
                      {liveMonitor.queue_balance.recent_claims.map((job) => (
                        <div
                          key={job.job_id}
                          className="flex items-center justify-between gap-3 p-2 rounded bg-muted/20 border border-border text-sm"
                          data-testid={`queue-balance-claim-${job.job_id}`}
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">{job.original_filename}</p>
                            <p className="text-xs text-muted-foreground">{job.processor_node_id}</p>
                          </div>
                          <Badge variant="outline" className="capitalize">
                            {job.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Active Jobs */}
            {liveMonitor.active_jobs && liveMonitor.active_jobs.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
                  {language === "es" ? "Jobs Activos" : "Active Jobs"} ({liveMonitor.active_jobs.length})
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {liveMonitor.active_jobs.map((job, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded bg-yellow-500/10 border border-yellow-500/20 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${
                          job.status === 'transcribing' ? 'bg-purple-500/20 text-purple-400' :
                          job.status === 'analyzing' ? 'bg-cyan-500/20 text-cyan-400' :
                          job.status === 'extracting_audio' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {job.status}
                        </Badge>
                        <span className="truncate max-w-[200px]">{job.original_filename}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">@{job.username}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Completed */}
            {liveMonitor.recent_completed && liveMonitor.recent_completed.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  {language === "es" ? "Completados Recientemente" : "Recently Completed"}
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {liveMonitor.recent_completed.slice(0, 5).map((job, idx) => (
                    <div key={idx} className="flex flex-col gap-2 rounded bg-green-500/5 border border-green-500/10 p-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span className="break-words sm:max-w-[250px] sm:truncate">{job.original_filename}</span>
                      <div className="flex items-center justify-between gap-2 sm:justify-end">
                        <span className="text-xs text-muted-foreground">
                          {job.duration_seconds ? formatDuration(job.duration_seconds) : '-'}
                        </span>
                        <span className="text-xs text-muted-foreground">@{job.username}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Tabs */}
        <div className="mb-6 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <Button
            variant={activeTab === "users" ? "default" : "outline"}
            onClick={() => setActiveTab("users")}
            className={`justify-between sm:justify-center ${activeTab === "users" ? "bg-primary" : ""}`}
            data-testid="admin-users-tab-button"
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </Button>
          <Button
            variant={activeTab === "videos" ? "default" : "outline"}
            onClick={() => setActiveTab("videos")}
            className={`justify-between sm:justify-center ${activeTab === "videos" ? "bg-primary" : ""}`}
            data-testid="admin-videos-tab-button"
          >
            <Video className="w-4 h-4 mr-2" />
            Videos
          </Button>
          <Button
            variant={activeTab === "reports" ? "default" : "outline"}
            onClick={() => setActiveTab("reports")}
            className={`justify-between sm:justify-center ${activeTab === "reports" ? "bg-primary" : ""}`}
            data-testid="admin-reports-tab-button"
          >
            <Bug className="w-4 h-4 mr-2" />
            {language === "es" ? "Reportes" : "Reports"}
            <Badge variant="secondary" className="ml-2" data-testid="admin-reports-tab-count">{reportsTotal}</Badge>
          </Button>
          <Button
            variant={activeTab === "reviews" ? "default" : "outline"}
            onClick={() => setActiveTab("reviews")}
            className={`justify-between sm:justify-center ${activeTab === "reviews" ? "bg-primary" : ""}`}
            data-testid="admin-reviews-tab-button"
          >
            <Star className="w-4 h-4 mr-2" />
            Reviews
            <Badge variant="secondary" className="ml-2" data-testid="admin-reviews-tab-count">{reviewsTotal}</Badge>
          </Button>
          <Button
            variant={activeTab === "error-logs" ? "default" : "outline"}
            onClick={() => setActiveTab("error-logs")}
            className={`justify-between sm:justify-center ${activeTab === "error-logs" ? "bg-primary" : ""}`}
            data-testid="admin-error-logs-tab-button"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            {language === "es" ? "Buzón de errores" : "Error mailbox"}
            <Badge variant={errorLogsUnseen > 0 ? "destructive" : "secondary"} className="ml-2" data-testid="admin-error-logs-tab-count">
              {errorLogsUnseen > 0 ? errorLogsUnseen : errorLogsTotal}
            </Badge>
          </Button>
        </div>

        <div className="mb-6 flex justify-end">
          <Button
            variant="outline"
            onClick={handleDownloadDianReport}
            disabled={downloadingDianReport}
            data-testid="admin-download-dian-report-button"
          >
            {downloadingDianReport ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {language === "es" ? "Descargar reporte DIAN (facturación)" : "Download DIAN billing report"}
          </Button>
        </div>

        {/* Users Table */}
        {activeTab === "users" && (
          <div className="space-y-4" data-testid="admin-users-tab">
            <Card className="bg-card border-border p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{language === "es" ? "Usuarios" : "Users"}</h3>
                  <p className="text-sm text-muted-foreground" data-testid="admin-users-count">
                    {language === "es"
                      ? `${filteredUsers.length} de ${users.length} usuarios visibles`
                      : `${filteredUsers.length} of ${users.length} users visible`}
                  </p>
                </div>
                <div className="relative w-full md:max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={userSearch}
                    onChange={(event) => setUserSearch(event.target.value)}
                    placeholder={language === "es" ? "Buscar por nombre o email" : "Search by name or email"}
                    className="pl-9"
                    data-testid="admin-users-search-input"
                  />
                </div>
              </div>
            </Card>

            {userGroups.map((group) => (
              <Card key={group.key} className="bg-card border-border overflow-hidden" data-testid={`admin-users-group-${group.key}`}>
                <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h4 className="font-semibold">{group.label}</h4>
                    <p className="text-xs text-muted-foreground">{group.users.length} {language === "es" ? "usuarios" : "users"}</p>
                  </div>
                  <Badge variant="outline">{group.users.length}</Badge>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {renderMobileUserCards(group.users, group.key)}
                </div>
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Membership</TableHead>
                        {group.key === "free" && (
                          <TableHead>{language === "es" ? "Duración subida" : "Uploaded duration"}</TableHead>
                        )}
                        <TableHead>{language === "es" ? "Videos únicos" : "Unique videos"}</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{renderUserRows(group.users, group.key)}</TableBody>
                  </Table>
                </div>
              </Card>
            ))}

            {userGroups.length === 0 && (
              <Card className="bg-card border-border p-8 text-center text-muted-foreground" data-testid="admin-users-empty-state">
                {language === "es" ? "No encontramos usuarios con ese criterio." : "No users found for that search."}
              </Card>
            )}
          </div>
        )}

        {/* Videos Table */}
        {activeTab === "videos" && (
          <Card className="bg-card border-border overflow-hidden">
            <div className="border-b border-border px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="admin-videos-toolbar">
              <div>
                <h3 className="text-lg font-semibold">{language === "es" ? "Videos y registros fantasma" : "Videos and ghost records"}</h3>
                <p className="text-sm text-muted-foreground" data-testid="admin-videos-toolbar-copy">
                  {language === "es"
                    ? "Limpia desde aquí el caché visible y los videos huérfanos detectados sin job real."
                    : "Clean visible cache and orphan video records without a real job from here."}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleAdminConsoleCleanup}
                disabled={consoleCleanupLoading}
                data-testid="admin-videos-cleanup-all-button"
              >
                {consoleCleanupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {language === "es" ? "Limpiar todo" : "Clean all"}
              </Button>
            </div>
            {videosLoading ? (
              <div className="p-10 flex items-center justify-center" data-testid="admin-videos-loading-state">
                <Loader2 className="w-7 h-7 animate-spin text-primary" />
              </div>
            ) : (
            <>
              <div className="space-y-3 p-4 md:hidden">
                {videos.map((video) => (
                  <div key={video.id} className="rounded-xl border border-border bg-background p-4 space-y-3" data-testid={`admin-video-card-${video.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold break-words">{video.original_filename}</p>
                        <p className="text-sm text-muted-foreground break-all">{video.user_email}</p>
                      </div>
                      <Badge variant="outline" className={`status-${video.status} text-xs font-mono shrink-0`}>{video.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">User</p>
                        <p className="font-medium">{video.username}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Duration</p>
                        <p className="font-medium">{video.duration_seconds ? formatDuration(video.duration_seconds) : "-"}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Created</p>
                        <p className="font-medium">{formatDate(video.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      {video.admin_cleanup_candidate ? (
                        <Badge variant="outline" className="text-amber-400 border-amber-400/30" data-testid={`admin-video-ghost-badge-${video.id}`}>
                          {language === "es" ? "Fantasma detectado" : "Ghost detected"}
                        </Badge>
                      ) : null}
                      {video.admin_cleanup_reason ? (
                        <span className="text-xs text-muted-foreground break-words" data-testid={`admin-video-ghost-reason-${video.id}`}>{video.admin_cleanup_reason}</span>
                      ) : null}
                    </div>
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!video.admin_cleanup_candidate || videoCleanupLoadingId === video.id}
                        onClick={() => handleCleanupSingleGhostVideo(video.id)}
                        data-testid={`admin-video-cleanup-button-${video.id}`}
                      >
                        {videoCleanupLoadingId === video.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                        {language === "es" ? "Limpiar este video" : "Clean this video"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>{language === "es" ? "Consola" : "Console"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {videos.map((video) => (
                      <TableRow key={video.id}>
                        <TableCell className="font-medium max-w-xs truncate">
                          {video.original_filename}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{video.username}</p>
                            <p className="text-xs text-muted-foreground">{video.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`status-${video.status} text-xs font-mono`}
                          >
                            {video.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {video.duration_seconds ? formatDuration(video.duration_seconds) : "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(video.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-start gap-2">
                            {video.admin_cleanup_candidate ? (
                              <Badge variant="outline" className="text-amber-400 border-amber-400/30" data-testid={`admin-video-ghost-badge-${video.id}`}>
                                {language === "es" ? "Fantasma" : "Ghost"}
                              </Badge>
                            ) : (
                              <Badge variant="outline" data-testid={`admin-video-clean-badge-${video.id}`}>
                                {language === "es" ? "Normal" : "Normal"}
                              </Badge>
                            )}
                            {video.admin_cleanup_reason ? (
                              <p className="text-xs text-muted-foreground max-w-xs" data-testid={`admin-video-ghost-reason-${video.id}`}>{video.admin_cleanup_reason}</p>
                            ) : null}
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!video.admin_cleanup_candidate || videoCleanupLoadingId === video.id}
                              onClick={() => handleCleanupSingleGhostVideo(video.id)}
                              data-testid={`admin-video-cleanup-button-${video.id}`}
                            >
                              {videoCleanupLoadingId === video.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                              {language === "es" ? "Limpiar este video" : "Clean this video"}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
            )}
          </Card>
        )}

        {activeTab === "reports" && (
          <div className="space-y-4" data-testid="admin-reports-tab">
            <Card className="bg-card border-border p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">{language === "es" ? "Inbox de reportes" : "Issue inbox"}</h3>
                  <p className="text-sm text-muted-foreground">
                    {language === "es"
                      ? "Revisa los problemas enviados por usuarios y los videos vinculados."
                      : "Review user-submitted issues and any linked videos."}
                  </p>
                </div>
                <Badge variant="outline" data-testid="admin-reports-total-badge">{reportsTotal}</Badge>
              </div>
            </Card>
            <IssueReportsPanel reports={issueReports} loading={reportsLoading} language={language} onDeleteReport={deleteIssueReport} />
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="space-y-4" data-testid="admin-reviews-tab">
            <Card className="bg-card border-border p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">{language === "es" ? "Inbox de reviews" : "Reviews inbox"}</h3>
                  <p className="text-sm text-muted-foreground">
                    {language === "es"
                      ? "Lee las reseñas y mensajes enviados por usuarios nuevos después de su transcripción de prueba."
                      : "Read ratings and messages submitted by new users after their trial transcription."}
                  </p>
                </div>
                <Badge variant="outline" data-testid="admin-reviews-total-badge">{reviewsTotal}</Badge>
              </div>
            </Card>
            <ReviewsPanel reviews={reviews} loading={reviewsLoading} language={language} onDeleteReview={deleteReview} />
          </div>
        )}

        {activeTab === "error-logs" && (
          <div className="space-y-4" data-testid="admin-error-logs-tab">
            <Card className="bg-card border-border p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold">{language === "es" ? "Buzón de errores" : "Error mailbox"}</h3>
                  <p className="text-sm text-muted-foreground">
                    {language === "es"
                      ? "Cuando un usuario ve “Se produjo un error” o falla una API, este panel guarda el contexto para reproducirlo."
                      : "When users hit “Something went wrong” or an API fails, this panel stores the context so you can reproduce it."}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" data-testid="admin-error-logs-total-badge">{errorLogsTotal}</Badge>
                  {errorLogsUnseen > 0 && <Badge variant="destructive" data-testid="admin-error-logs-unseen-badge">{errorLogsUnseen}</Badge>}
                </div>
              </div>
            </Card>
            <ErrorLogsPanel
              logs={errorLogs}
              loading={errorLogsLoading}
              language={language}
              unseenTotal={errorLogsUnseen}
              onMarkSeen={(logIds) => markErrorLogsSeen(logIds, false)}
              onMarkAllSeen={() => markErrorLogsSeen([], true)}
              onDeleteSelected={deleteSelectedErrorLogs}
              deletingSelected={deletingErrorLogs}
            />
          </div>
        )}

        {/* User Details Dialog */}
        {selectedUser && selectedUserData && (
          <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>User Details: {selectedUserData.username}</DialogTitle>
                <DialogDescription>{selectedUserData.email}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="p-3 bg-muted rounded">
                    <p className="text-xs text-muted-foreground">Membership</p>
                    <p className="font-semibold capitalize">{selectedUserData.membership}</p>
                  </div>
                  <div className="p-3 bg-muted rounded">
                    <p className="text-xs text-muted-foreground">
                      {language === "es" ? "Videos únicos completados" : "Unique completed videos"}
                    </p>
                    <p className="font-semibold">{selectedUser.video_count}</p>
                  </div>
                  <div className="p-3 bg-muted rounded">
                    <p className="text-xs text-muted-foreground">Video Duration</p>
                    <p className="font-semibold">
                      {formatDuration(selectedUserData.total_video_duration)}
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-4" data-testid="admin-user-detail-account-card">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h4 className="font-semibold" data-testid="admin-user-detail-account-title">
                        {language === "es" ? "Datos de la cuenta" : "Account details"}
                      </h4>
                      <p className="text-sm text-muted-foreground" data-testid="admin-user-detail-account-subtitle">
                        {language === "es"
                          ? "Información capturada durante el registro del usuario."
                          : "Information captured when the user created the account."}
                      </p>
                    </div>
                    <Badge variant={selectedUserData.email_verified ? "default" : "outline"} data-testid="admin-user-detail-email-verified-badge">
                      {selectedUserData.email_verified
                        ? (language === "es" ? "Correo verificado" : "Email verified")
                        : (language === "es" ? "Correo pendiente" : "Email pending")}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-first-name">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Nombre" : "First name"}</p>
                      <p className="mt-1 font-semibold break-words">{selectedUserData.first_name || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-last-name">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Apellido" : "Last name"}</p>
                      <p className="mt-1 font-semibold break-words">{selectedUserData.last_name || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3 sm:col-span-2" data-testid="admin-user-detail-field-full-name">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Nombre completo" : "Full name"}</p>
                      <p className="mt-1 font-semibold break-words">{selectedUserData.full_name || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3 sm:col-span-2" data-testid="admin-user-detail-field-email">
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="mt-1 font-semibold break-all">{selectedUserData.email || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-phone-country-iso2">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "País" : "Country"}</p>
                      <p className="mt-1 font-semibold uppercase">{selectedUserData.phone_country_iso2 || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-phone-country-code">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Código país" : "Country code"}</p>
                      <p className="mt-1 font-semibold">{selectedUserData.phone_country_code || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-phone-number">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Teléfono" : "Phone number"}</p>
                      <p className="mt-1 font-semibold break-all">{selectedUserData.phone_number || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-phone-e164">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Teléfono completo" : "Full phone"}</p>
                      <p className="mt-1 font-semibold break-all">{selectedUserData.phone_e164 || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-created-at">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Fecha de registro" : "Registered at"}</p>
                      <p className="mt-1 font-semibold">{selectedUserData.created_at ? formatDate(selectedUserData.created_at) : "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-email-verified-at">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Correo verificado el" : "Email verified at"}</p>
                      <p className="mt-1 font-semibold">{selectedUserData.email_verified_at ? formatDate(selectedUserData.email_verified_at) : "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-terms-accepted">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Términos aceptados" : "Terms accepted"}</p>
                      <p className="mt-1 font-semibold">{selectedUserData.terms_accepted ? (language === "es" ? "Sí" : "Yes") : (language === "es" ? "No" : "No")}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3" data-testid="admin-user-detail-field-terms-version">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Versión de términos" : "Terms version"}</p>
                      <p className="mt-1 font-semibold break-words">{selectedUserData.terms_version || "-"}</p>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3 sm:col-span-2" data-testid="admin-user-detail-field-terms-accepted-at">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Términos aceptados el" : "Terms accepted at"}</p>
                      <p className="mt-1 font-semibold">{selectedUserData.terms_accepted_at ? formatDate(selectedUserData.terms_accepted_at) : "-"}</p>
                    </div>
                  </div>
                </div>
                <div
                  className="rounded-2xl border border-border bg-muted/40 p-4"
                  data-testid="admin-user-detail-usage-card"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground" data-testid="admin-user-detail-usage-title">
                        {getUsageTitle(selectedUser.usage)}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground" data-testid="admin-user-detail-usage-subtitle">
                        {getUsageSubtitle(selectedUser.usage)}
                      </p>
                    </div>
                    <Badge variant="outline" className="w-fit capitalize" data-testid="admin-user-detail-usage-membership-badge">
                      {selectedUser.usage?.is_admin
                        ? (language === "es" ? "admin" : "admin")
                        : (selectedUser.usage?.membership || selectedUserData.membership || "free")}
                    </Badge>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-xl bg-background p-3" data-testid="admin-user-detail-usage-used-block">
                      <p className="text-xs text-muted-foreground">{language === "es" ? "Usado" : "Used"}</p>
                      <p className="mt-1 text-lg font-semibold" data-testid="admin-user-detail-usage-used-value">
                        {formatUsageTime(selectedUser.usage?.minutes_used, selectedUser.usage?.is_admin)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-background p-3" data-testid="admin-user-detail-usage-limit-block">
                      <p className="text-xs text-muted-foreground">
                        {selectedUser.usage?.lifetime_limit
                          ? (language === "es" ? "Límite" : "Limit")
                          : (language === "es" ? "Límite diario" : "Daily limit")}
                      </p>
                      <p className="mt-1 text-lg font-semibold" data-testid="admin-user-detail-usage-limit-value">
                        {formatUsageTime(selectedUser.usage?.daily_limit, selectedUser.usage?.is_admin)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-background p-3" data-testid="admin-user-detail-usage-remaining-block">
                      <p className="text-xs text-muted-foreground">
                        {selectedUser.usage?.lifetime_limit
                          ? (language === "es" ? "Restante" : "Remaining")
                          : (language === "es" ? "Le queda hoy" : "Left today")}
                      </p>
                      <p className="mt-1 text-lg font-semibold" data-testid="admin-user-detail-usage-remaining-value">
                        {formatUsageTime(selectedUser.usage?.remaining_minutes, selectedUser.usage?.is_admin)}
                      </p>
                    </div>
                  </div>
                </div>
                {selectedUser.videos.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Recent Videos</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {selectedUser.videos.slice(0, 10).map((video) => (
                        <div
                          key={video.id}
                          className="flex flex-col gap-3 rounded bg-muted/50 p-2 text-sm sm:flex-row sm:items-center sm:justify-between"
                          data-testid={`admin-user-detail-video-${video.id}`}
                        >
                          <div className="min-w-0">
                            <span className="block break-words sm:max-w-xs sm:truncate">{video.original_filename}</span>
                            <p className="text-xs text-muted-foreground" data-testid={`admin-user-detail-video-duration-${video.id}`}>
                              {video.duration_seconds ? formatDuration(video.duration_seconds) : "-"}
                            </p>
                          </div>
                          <Badge variant="outline" className={`status-${video.status}`}>
                            {video.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Manual plan change on an account that pays a subscription */}
        <Dialog
          open={!!pendingMembershipChange}
          onOpenChange={(open) => {
            if (!open && !changingMembership) setPendingMembershipChange(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {language === "es" ? "Este cambio cancela su suscripción" : "This change cancels their subscription"}
              </DialogTitle>
              {pendingMembershipChange && (
                <DialogDescription data-testid="membership-change-subscription-warning">
                  {language === "es"
                    ? `${pendingMembershipChange.user.username} paga una suscripción en Mercado Pago (${describeSubscriptions(pendingMembershipChange.subscriptions)}). Si le pones el plan ${pendingMembershipChange.membership} a mano, esa suscripción se cancela y deja de pagar.`
                    : `${pendingMembershipChange.user.username} pays a Mercado Pago subscription (${describeSubscriptions(pendingMembershipChange.subscriptions)}). Setting the ${pendingMembershipChange.membership} plan by hand cancels it and they stop paying.`}
                </DialogDescription>
              )}
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingMembershipChange(null)} disabled={changingMembership}>
                {language === "es" ? "Dejarlo como está" : "Leave as is"}
              </Button>
              <Button
                onClick={() => handleMembershipChange(pendingMembershipChange.user.id, pendingMembershipChange.membership, true)}
                disabled={changingMembership}
                data-testid="confirm-membership-change-button"
              >
                {changingMembership && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {language === "es" ? "Cambiar plan y cancelar suscripción" : "Change plan and cancel subscription"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete user "{userToDelete?.username}"? This will also
                delete all their videos. This action cannot be undone.
              </DialogDescription>
              {userToDeleteSubscriptions === null && (
                <p className="text-sm text-muted-foreground">
                  {language === "es" ? "Consultando sus suscripciones en Mercado Pago…" : "Checking their Mercado Pago subscriptions…"}
                </p>
              )}
              {userToDeleteSubscriptions?.length > 0 && (
                <p className="text-sm font-medium text-orange-400" data-testid="delete-user-subscription-warning">
                  {language === "es"
                    ? `Este usuario paga una suscripción en Mercado Pago (${describeSubscriptions(userToDeleteSubscriptions)}). Al eliminarlo se cancela y no se le vuelve a cobrar.`
                    : `This user pays a Mercado Pago subscription (${describeSubscriptions(userToDeleteSubscriptions)}). Deleting them cancels it and they are not charged again.`}
                </p>
              )}
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deletingUser}>
                {language === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteUser}
                disabled={deletingUser || userToDeleteSubscriptions === null}
                data-testid="confirm-delete-user-button"
              >
                {deletingUser ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {language === "es" ? "Eliminando..." : "Deleting..."}
                  </>
                ) : (
                  language === "es" ? "Eliminar usuario" : "Delete User"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
